import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import NavBar from "../NavBar";
import UserCard from "./UserCard";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import {
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  CoachCardWrapper,
  ScrollLoader,
} from "./styles";
import UserProfileStore from "stores/UserProfileStore";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Coach extends React.Component<Props> {
  @computed get profiles() {
    return UserProfileStore.items;
  }

  @computed get profileListIsEmpty() {
    return this.profiles.length === 0;
  }

  componentDidMount() {
    UserProfileStore.fetch();
  }

  componentWillUnmount() {
    UserProfileStore.resetPage();
  }

  fetchMoreUsers = async () => {
    const { setMessage } = this.props;

    try {
      await UserProfileStore.fetchMoreUsers();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    UserProfileStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && UserProfileStore.fetch();
  };

  onSearchClear = () => {
    UserProfileStore.clearSearch();
    UserProfileStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    UserProfileStore.applyOrder(value);
    UserProfileStore.resetPage();
    UserProfileStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      UserProfileStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    UserProfileStore.resetPage();
    UserProfileStore.fetch();
  };

  onFilterClear = () => {
    UserProfileStore.clearFilters();
    UserProfileStore.fetch();
  };

  onDetailsClick = (coach: any) => () => {
    const path = generatePath(ROUTES.CONTENT_PROFILE_USER_DETAIL, {
      userId: coach.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  render() {
    return (
      <Page
        title="Users"
        topControls={<NavBar />}
        isLoading={UserProfileStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={UserProfileStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={UserProfileStore.orderContext.appliedOrderInfo}
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={UserProfileStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {!this.profileListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.profiles.length}
            hasMore={UserProfileStore.hasMoreInfo}
            next={this.fetchMoreUsers}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.profiles.map(profile => (
              <CoachCardWrapper key={profile.id}>
                <UserCard
                  title={profile.username}
                  onDetailsClick={this.onDetailsClick(profile)}
                />
              </CoachCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              UserProfileStore.search
                ? "No matches found. Please try another search."
                : "No users added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Coach)));
